import { useEffect, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { BusFront, CheckCircle2, Send, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUP_SIZES = ["7–15 people", "16–30 people", "31–50 people", "51–100 people", "100–200 people"];
const EMPTY = { name: "", email: "", phone: "", date: "", group_size: "", route: "", message: "" };

export const GroupQuoteModal = ({ open, onClose, initialMessage = "" }) => {
  const [form, setForm] = useState({ ...EMPTY, message: initialMessage });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls =
    "w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-medium outline-none focus:ring-2 focus:ring-[var(--brand)]/30 focus:border-[var(--brand)] transition-all";

  // Re-prefill whenever the modal opens for a different vehicle
  useEffect(() => {
    if (open) setForm((f) => ({ ...f, message: initialMessage || f.message }));
  }, [open, initialMessage]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.group_size || !form.route.trim()) {
      toast.error("Please fill in name, email, group size and route.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        guests: form.group_size,
        route: form.route,
        pickup_date: form.date || null,
        message: form.message || null,
        type: "group_quote",
      });
      setDone(true);
      toast.success("Request received — we reply within 24 hours.");
    } catch {
      toast.error("Could not send your request. Please try again or email us.");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => {
      setDone(false);
      setForm(EMPTY);
    }, 300);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          data-testid="group-quote-modal"
        >
          <div className="absolute inset-0 bg-[#0b132b]/50 backdrop-blur-sm" onClick={close} />
          <motion.div
            initial={{ y: 24, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 24, opacity: 0, scale: 0.97 }}
            className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden overscroll-contain [touch-action:pan-y] rounded-3xl bg-white p-6 sm:p-8 shadow-2xl"
          >
            <button
              onClick={close}
              data-testid="group-quote-close"
              className="absolute top-4 right-4 w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            {done ? (
              <div className="py-10 text-center" data-testid="group-quote-success">
                <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500" />
                <h3 className="mt-4 font-display font-bold text-2xl text-[#0b132b]">Request received!</h3>
                <p className="mt-2 text-sm text-slate-500 max-w-sm mx-auto">
                  Our group specialists will send your tailored quote within 24 hours — usually much faster.
                </p>
                <button
                  data-testid="group-quote-done-close"
                  onClick={close}
                  className="mt-6 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-7 py-3 shadow-lg shadow-[var(--brand)]/30 hover:bg-[var(--brand-dark)] transition-colors"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="group-quote-form">
                <div>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand-soft)] px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-[var(--brand)]">
                    <BusFront className="w-3.5 h-3.5" /> 7–200 People
                  </span>
                  <h3 className="mt-3 font-display font-bold text-2xl text-[#0b132b]">Request a Group Quote</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Clubs, companies, schools and events — tell us the basics and we'll send a tailored quote within 24 hours.
                  </p>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="group-quote-name" value={form.name} onChange={set("name")} placeholder="Your name *" className={inputCls} />
                  <input data-testid="group-quote-email" type="email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input data-testid="group-quote-phone" value={form.phone} onChange={set("phone")} placeholder="Phone / WhatsApp" className={inputCls} />
                  <input data-testid="group-quote-date" type="date" value={form.date} onChange={set("date")} className={`${inputCls} ${form.date ? "text-[#0b132b]" : "text-slate-400"}`} aria-label="Travel date" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <select
                    data-testid="group-quote-size"
                    value={form.group_size}
                    onChange={set("group_size")}
                    className={`${inputCls} ${form.group_size ? "text-[#0b132b]" : "text-slate-400"}`}
                  >
                    <option value="">Group size *</option>
                    {GROUP_SIZES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <input data-testid="group-quote-route" value={form.route} onChange={set("route")} placeholder="Route (e.g. Zurich → Zermatt) *" className={inputCls} />
                </div>
                <textarea
                  data-testid="group-quote-message"
                  rows={3}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Anything else? Occasion, luggage, stops, schedule…"
                  className={`${inputCls} resize-none`}
                />
                <button
                  data-testid="group-quote-submit"
                  disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold py-3.5 shadow-lg shadow-[var(--brand)]/30 hover:bg-[var(--brand-dark)] transition-colors disabled:opacity-60"
                >
                  <Send className="w-4 h-4" /> {busy ? "Sending…" : "Request Quote"}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
